/**
 * Signalling messages for a huddle call (h1-plan.md section 2).
 *
 * Every payload arrives over the private Realtime channel huddle:<id> as the
 * broadcast event 'sig'. It is untrusted until parseSignal says otherwise:
 * wrong version, unknown type, a missing field or an oversized payload is
 * dropped, never answered. from.sessionId is self-declared, so the roster
 * filter in the mesh is a filter, not authentication (h1-plan.md:185).
 */

export const SIGNAL_EVENT = 'sig';
export const SIGNAL_VERSION = 1;
/** An audio-only SDP is a few KB; anything bigger is not ours. */
export const SIGNAL_MAX_BYTES = 64 * 1024;

export interface SignalFrom {
  userId: string;
  sessionId: string;
}

export interface IceCandidateJson {
  candidate: string;
  sdpMid: string | null;
  sdpMLineIndex: number | null;
  usernameFragment?: string | null;
}

export type Signal =
  | { v: 1; type: 'hello'; from: SignalFrom; name: string }
  | { v: 1; type: 'offer'; from: SignalFrom; to: string; sdp: string }
  | { v: 1; type: 'answer'; from: SignalFrom; to: string; sdp: string }
  | { v: 1; type: 'ice'; from: SignalFrom; to: string; candidate: IceCandidateJson | null }
  | { v: 1; type: 'mute'; from: SignalFrom; muted: boolean }
  | { v: 1; type: 'bye'; from: SignalFrom }
  | { v: 1; type: 'roster'; from: SignalFrom };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown, max = SIGNAL_MAX_BYTES): value is string {
  return typeof value === 'string' && value.trim() !== '' && value.length <= max;
}

function parseFrom(value: unknown): SignalFrom | null {
  if (!isRecord(value)) return null;
  const { userId, sessionId } = value;
  if (!isText(userId, 64) || !isText(sessionId, 64)) return null;
  return { userId, sessionId };
}

function parseCandidate(value: unknown): IceCandidateJson | null | undefined {
  if (value === null) return null; // end of candidates
  if (!isRecord(value)) return undefined;
  const { candidate, sdpMid, sdpMLineIndex, usernameFragment } = value;
  if (typeof candidate !== 'string') return undefined;
  if (!(typeof sdpMid === 'string' || sdpMid === null || sdpMid === undefined)) return undefined;
  if (!(typeof sdpMLineIndex === 'number' || sdpMLineIndex === null || sdpMLineIndex === undefined))
    return undefined;
  if (!(
    typeof usernameFragment === 'string' ||
    usernameFragment === null ||
    usernameFragment === undefined
  ))
    return undefined;
  return {
    candidate,
    sdpMid: sdpMid ?? null,
    sdpMLineIndex: sdpMLineIndex ?? null,
    usernameFragment: usernameFragment ?? null,
  };
}

function sizeOf(value: unknown): number {
  try {
    return JSON.stringify(value)?.length ?? Infinity;
  } catch {
    return Infinity;
  }
}

/** The validated message, or null for anything that is not exactly one of ours. */
export function parseSignal(payload: unknown): Signal | null {
  if (!isRecord(payload)) return null;
  if (payload.v !== SIGNAL_VERSION) return null;
  if (sizeOf(payload) > SIGNAL_MAX_BYTES) return null;
  const from = parseFrom(payload.from);
  if (from === null) return null;
  const v = SIGNAL_VERSION;
  switch (payload.type) {
    case 'hello': {
      const name = typeof payload.name === 'string' ? payload.name.slice(0, 120) : '';
      return { v, type: 'hello', from, name };
    }
    case 'offer':
    case 'answer': {
      if (!isText(payload.to, 64) || !isText(payload.sdp)) return null;
      return { v, type: payload.type, from, to: payload.to, sdp: payload.sdp };
    }
    case 'ice': {
      if (!isText(payload.to, 64)) return null;
      const candidate = parseCandidate(payload.candidate);
      if (candidate === undefined) return null;
      return { v, type: 'ice', from, to: payload.to, candidate };
    }
    case 'mute':
      if (typeof payload.muted !== 'boolean') return null;
      return { v, type: 'mute', from, muted: payload.muted };
    case 'bye':
      return { v, type: 'bye', from };
    case 'roster':
      return { v, type: 'roster', from };
    default:
      return null;
  }
}

/** A message addressed to someone else is not ours to act on. */
export function isForMe(signal: Signal, mySessionId: string): boolean {
  return !('to' in signal) || signal.to === mySessionId;
}

/**
 * Who offers (no glare, h1-plan.md:198): the people already in the call offer
 * to a newcomer when its hello arrives; the newcomer only answers. So on a
 * hello, I offer exactly when I am already in the call.
 */
export function shouldOfferOnHello(iAmAlreadyInCall: boolean): boolean {
  return iAmAlreadyInCall;
}

/** ICE restart after 5 s disconnected: the smaller sessionId offers. */
export function restartOfferer(mySessionId: string, peerSessionId: string): boolean {
  return mySessionId < peerSessionId;
}
